import {
	createContext,
	useContext,
	ReactNode,
	useState,
	useEffect,
} from 'react';
import { IPremadeQuiz } from '@quizzach/interfaces';
import { useUserQuizzes } from '@quizzach/api-hooks';

type QuizOwnershipContextType = {
	isQuizOwned: (quiz: IPremadeQuiz) => boolean;
	getUserQuizUuid: (premadeQuiz: IPremadeQuiz) => string | undefined;
	getPremadeQuizUuid: (userQuizUuid: string) => string | undefined;
	uuidSet: Set<string>;
};

const QuizOwnershipContext = createContext<
	QuizOwnershipContextType | undefined
>(undefined);

export const UserOwnedPremadeQuizzesProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const { quizzesToList } = useUserQuizzes();
	const [uuidSet, setUuidSet] = useState<Set<string>>(new Set());
	const [premadeToUserUuidMap, setPremadeToUserUuidMap] = useState<
		Map<string, string>
	>(new Map());
	const [userToPremadeUuidMap, setUserToPremadeUuidMap] = useState<
		Map<string, string>
	>(new Map());

	useEffect(() => {
		if (quizzesToList) {
			const newUuidSet = new Set<string>();
			const newPremadeToUserUuidMap = new Map<string, string>(); // premade UUID -> user quiz UUID
			const newUserToPremadeUuidMap = new Map<string, string>(); // user quiz UUID -> premade UUID

			quizzesToList
				.filter((quiz) => quiz.quiz_type === 'premade')
				.forEach((quiz) => {
					const premadeUuid = (quiz.premade_quiz as { uuid: string }).uuid;
					newUuidSet.add(premadeUuid);
					newPremadeToUserUuidMap.set(premadeUuid, quiz.uuid);
					newUserToPremadeUuidMap.set(quiz.uuid, premadeUuid); // Reverse mapping
				});

			setUuidSet(newUuidSet);
			setPremadeToUserUuidMap(newPremadeToUserUuidMap);
			setUserToPremadeUuidMap(newUserToPremadeUuidMap);
		}
	}, [quizzesToList]);

	const isQuizOwned = (premadeUuid: IPremadeQuiz): boolean => {
		return uuidSet.has(premadeUuid.uuid);
	};

	const getUserQuizUuid = (premadeQuiz: IPremadeQuiz) => {
		return premadeToUserUuidMap.get(premadeQuiz.uuid);
	};

	const getPremadeQuizUuid = (userQuizUuid: string) => {
		return userToPremadeUuidMap.get(userQuizUuid);
	};

	return (
		<QuizOwnershipContext.Provider
			value={{ isQuizOwned, getUserQuizUuid, getPremadeQuizUuid, uuidSet }}
		>
			{children}
		</QuizOwnershipContext.Provider>
	);
};

export const useQuizOwnershipContext = () => {
	const context = useContext(QuizOwnershipContext);
	if (context === undefined) {
		throw new Error(
			'useQuizOwnershipContext must be used within a QuizOwnershipContextProvider'
		);
	}
	return context;
};
